import React, { forwardRef, Suspense, useRef } from "react";
import { IoIosSend } from "react-icons/io";
import { CiFaceSmile } from "react-icons/ci";
import { LuImagePlus } from "react-icons/lu";
import { Button, Form, InputGroup } from "react-bootstrap";

import {
  fileFormat,
  emptyString,
  STYLES,
  WINDOW_EVENTS,
  EMPTY_ARRAY_LENGTH,
  BOOTSTRAP_SPINNER_SIZE,
  inboxReduxConstants,
  INPUT_FIELD_ICON_SIZE,
} from "../../../constants";
import useAutosizeTextArea from "../../../../../shared/components/hooks/useAutoResizeTextArea/useAutoResizeTextArea";
import { useEventListener } from "../../../../../shared/components/hooks/useEventListener/useEventListener";
import { getConditionalValues } from "../../../../../shared";
import GenerateAIResponseButton from "./generateAIResponseButton";
import BootstrapSpinner from "./bootstrapSpinner";
import EmojiPickerPanel from "./emojiPickerPanel";

const InputFieldsComponent = forwardRef((props, ref) => {
  const {
    files,
    message,
    spinner,
    isSending,
    sendMessage,
    isSuperAdmin,
    isAIReplyActive,
    showEmojiPicker,
    handleFileChange,
    handleEmojiClick,
    setShowEmojiPicker,
    generateAiResponse,
    handleMessageChange,
    selectedConversation,
    isAIGenerationButtonVisible,
  } = props;

  const { inputFieldRef, fileUploaderRef } = ref;

  const emojiPickerRef = useRef(null);
  const emojiShowButton = useRef(null);

  useAutosizeTextArea(inputFieldRef.current, message);

  const isEmojiPicker = (event) => {
    return (
      emojiPickerRef?.current?.contains(event.target) === false &&
      emojiShowButton?.current?.contains(event.target) === false
    );
  };

  //Close emoji picker when click outside
  const handleClickOutside = (event) => {
    if (isEmojiPicker(event)) {
      // Clicked outside the emoji picker, close it
      setShowEmojiPicker(false);
    }
  };

  const isLoading = spinner(inboxReduxConstants.GENERATE_AI_RESPONSE);

  //Handle emoji click
  const handleEmojies = () => {
    const isShowEmojiPicker = !isSending && !isSuperAdmin;
    if (isShowEmojiPicker) {
      setShowEmojiPicker(!showEmojiPicker);
    }
  };

  useEventListener(WINDOW_EVENTS.MOUSEDOWN, handleClickOutside, document);

  return (
    <div
      className={`mx-3 ${
        Object.keys(selectedConversation).length === EMPTY_ARRAY_LENGTH &&
        STYLES.D_NONE
      }`}
    >
      {isAIReplyActive() && (
        <Suspense fallback={<div>Loading Generate AI Response Button</div>}>
          <GenerateAIResponseButton
            spinner={spinner}
            isSending={isSending}
            isSuperAdmin={isSuperAdmin}
            generateAiResponse={() => generateAiResponse(false)}
            isAIGenerationButtonVisible={isAIGenerationButtonVisible}
          />
        </Suspense>
      )}

      <div className="d-flex gap-2 align-items-center w-100 mt-auo">
        <InputGroup
          className={`border border-2 rounded-5 d-flex align-items-center ${
            isSuperAdmin && " bg-secondary bg-opacity-10 opacity-50"
          }`}
          aria-disabled={isSuperAdmin}
        >
          <InputGroup.Text
            className="bg-transparent border-0 align-self-end px-2"
            ref={emojiShowButton}
          >
            <CiFaceSmile
              size={INPUT_FIELD_ICON_SIZE}
              onClick={handleEmojies}
              data-testid="emoji-picker-icon"
              className={`fw-bold text-black ${getConditionalValues(
                isSuperAdmin,
                "cursor-disabled",
                "pointer"
              )}`}
            />
          </InputGroup.Text>
          <Form.Control
            rows={1}
            as="textarea"
            value={message}
            ref={inputFieldRef}
            onChange={(e) => {
              handleMessageChange(e);
            }}
            disabled={isSending || isSuperAdmin}
            id="message-text-area"
            placeholder="type message here"
            className={`message-text-area custome-sidebar border-0 px-0 my-auto bg-transparent ${
              isSuperAdmin && "cursor-disabled"
            }`}
          />
        </InputGroup>
        {showEmojiPicker && (
          <EmojiPickerPanel
            emojiPickerRef={emojiPickerRef}
            onEmojiClick={handleEmojiClick}
          />
        )}
        <div className="m-1 d-flex align-items-center justify-content-end gap-2 align-self-end">
          <Form.Control
            multiple
            type="file"
            id="uploadBtn"
            value={emptyString}
            disabled={isSending || isSuperAdmin}
            ref={fileUploaderRef}
            onChange={handleFileChange}
            accept={fileFormat.IMG_TYPES}
            data-testid="attach-file-field"
          />
          <label
            name="Img"
            htmlFor="uploadBtn"
            className={`shadow-none bg-secondary bg-opacity-25 p-2 rounded-pill ${getConditionalValues(
              isSuperAdmin,
              "cursor-disabled opacity-50",
              "pointer"
            )}`}
          >
            <LuImagePlus
              size={INPUT_FIELD_ICON_SIZE}
              data-testid="attach-file-icon"
              className="fw-bold text-black"
            />
          </label>
        </div>
        <div className="align-self-end">
          {(message?.trim() || files.length > EMPTY_ARRAY_LENGTH) && (
            <Button
              disabled={
                (!message.trim() && !files.length) ||
                isSending ||
                isSuperAdmin ||
                isLoading
              }
              onClick={() => sendMessage(false)}
              variant="success"
              data-testid="send-button"
              className="btn-sm green-theme text-white border-0 fw-bold rounded-pill mb-1 send-button "
            >
              {isSending ? (
                <BootstrapSpinner
                  size={BOOTSTRAP_SPINNER_SIZE.SM}
                  className="mt-1"
                />
              ) : (
                <IoIosSend size={INPUT_FIELD_ICON_SIZE} />
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
});

export default React.memo(InputFieldsComponent);
